import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { FKType } from './SQLBuilder'
import { User } from './User'

type SubscriberConstructorArgs = ConstructorArgs<() => Subscriber>

export class Subscriber extends BaseRecord {
    readonly recordType = 'subscriber'

    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    readonly plan?: string
    readonly paidDate?: moment.Moment
    readonly nextInvoiceDate?: moment.Moment

    constructor(data?: Partial<SubscriberConstructorArgs>) {
        super(data)
        if (!data) {
            this.creator = PSApplication.me()
            this.owner = this.creator.owner
            return
        }

        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.plan = data.plan ?? undefined
        this.paidDate = this.propertyFromTimestampArg(data.paid_date)
        this.nextInvoiceDate = this.propertyFromDateArg(data.next_invoice_date)
    }

    clone(overrides?: Partial<SubscriberConstructorArgs>): Subscriber {
        return new Subscriber({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Subscriber {
        return (
            (CacheFactory.getInstance().getRecord(Subscriber, id) as Subscriber) ??
            new Subscriber({ id })
        )
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'person', 'creator'],
    }

    static dataType(): DataType {
        return 'subscribers'
    }

    jsonObject(): any {
        const jsonObject = super.jsonObject()
        jsonObject['owner'] = this.owner?.id
        jsonObject['person'] = this.person?.id
        return jsonObject
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
}
